// src/hooks/usePanelEnrichment.js
//
// Fetches enriched panel data for a selected GeoJSON well feature.
// Sniffs the feature for a usable RRC identifier in priority order:
//   asset_id → api_10 → api10 → api_8 → feature.id
//
// Returns { data, loading, error, assetId }
//
// • 80ms debounce prevents thrash on fast map clicks
// • AbortController cancels in-flight requests on feature change
// • Null feature short-circuits immediately — no fetch

import { useEffect, useRef, useState } from 'react';
import { fetchWithAuth } from '../api/fetchWithAuth';

import { getConfig } from '../config';

const baseUrl = () => getConfig().panelApiUrl ?? '';

const DEBOUNCE_MS = 80;

function sniffIdentifier(feature) {
  if (!feature) return null;
  const p = feature.properties ?? {};

  // Priority order — extend this list as you discover real field names
  // Priority: UUID asset_id first (matches vector_assets_global PK),
  // then RRC API numbers as fallback for older tile layers.
  const candidates = [
    p.asset_id,
    p.id,                                                    // properties.id UUID
    typeof feature.id === 'string' ? feature.id : null,      // feature.id UUID
    p.api_10,
    p.api10,
    p.api_8,
    p.api8,
    p.api,                                                   // 8-digit RRC source_id
  ];

  const found = candidates.find((c) => c != null && String(c).trim() !== '');
  if (!found) {
    console.warn(
      '[usePanelEnrichment] No usable identifier found on feature. ' +
      'Available properties:', Object.keys(p)
    );
  }
  return found ? String(found).trim() : null;
}

export function usePanelEnrichment(feature) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [assetId, setAssetId] = useState(null);

  const abortRef = useRef(null);
  const timerRef = useRef(null);

  const BASE_URL = getConfig().panelApiUrl ?? '';

  useEffect(() => {
    // Clear previous state immediately when feature changes
    setData(null);
    setError(null);

    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    const id = sniffIdentifier(feature);
    setAssetId(id);

    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);

    timerRef.current = setTimeout(async () => {
      const ac = new AbortController();
      abortRef.current = ac;

      try {
        const res = await fetchWithAuth(
          `${BASE_URL}/api/v1/panel/${encodeURIComponent(id)}`,
          { signal: ac.signal }
        );

        if (!res.ok) {
          const body = await res.json().catch((e) => { console.error("[ui/src/hooks/usePanelEnrichment.js] res.json() failed (swallowed):", e); return {}; });
          throw new Error(body.detail ?? `HTTP ${res.status}`);
        }

        const json = await res.json();
        if (json.error) throw new Error(json.error);

        setData(json);
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message ?? 'Failed to load well data');
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [feature]);

  return { data, loading, error, assetId };
}